import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useLocation } from 'react-router-dom';
import { shouldDisplayAds } from './InitAdmob';
import { useShowMembershipPopup } from './useShowMembershipPopup';
import { ANDROID_APP_URL, IOS_APP_URL } from './utils';

const PAGE_VIEW_KEY = 'pageViewCount';
const LAST_SHOWN_KEY = 'smartAppDialogLastShown';
const MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000;

type DeviceInfo = {
  os: 'ios' | 'android';
  storeName: string;
  storeLink: string;
} | null;

export const useSmartAppDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(null);
  const location = useLocation();
  const membershipPopupVisible = useShowMembershipPopup();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      return; // Exit if in a native app environment
    }
    if (!shouldDisplayAds(location.pathname)) {
      return; // Exit if ads should not be displayed on this page
    }
    // If membership popup is visible, never show the smart app dialog
    if (membershipPopupVisible) {
      return;
    }
    // 1. FREQUENCY CHECK: Has it been shown in the last month?
    const lastShownTimestamp = localStorage.getItem(LAST_SHOWN_KEY);
    if (lastShownTimestamp && Date.now() - parseInt(lastShownTimestamp) < MONTH_IN_MS) {
      return; // Exit if shown recently
    }

    // 2. PAGE VIEW CHECK: Is this the second page view of the session?
    const currentCount = parseInt(sessionStorage.getItem(PAGE_VIEW_KEY) || '0');
    const newCount = currentCount + 1;
    sessionStorage.setItem(PAGE_VIEW_KEY, newCount.toString());

    if (newCount < 2) {
      return; // Exit if it's the first page view
    }

    // 3. DEVICE DETECTION
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let detectedOS: 'ios' | 'android' | null = null;

    if (/android/i.test(userAgent)) {
      detectedOS = 'android';
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      detectedOS = 'ios';
    }

    if (!detectedOS) {
      return; // Exit if not on a mobile device
    }

    // 4. ALL CHECKS PASSED: Prepare device-specific info and trigger the dialog
    if (detectedOS === 'ios') {
      setDeviceInfo({
        os: 'ios',
        storeName: 'App Store',
        storeLink: IOS_APP_URL,
      });
    } else {
      setDeviceInfo({
        os: 'android',
        storeName: 'Google Play',
        storeLink: ANDROID_APP_URL,
      });
    }

    setIsOpen(true);
    // IMPORTANT: Record that we've shown the dialog now.
    localStorage.setItem(LAST_SHOWN_KEY, Date.now().toString());
  }, [location.pathname, membershipPopupVisible]); // Re-run when navigating or when membership popup visibility changes

  return { isOpen, setIsOpen, deviceInfo };
};
